import { useCallback, useState } from 'react'

import {
  IconSegment,
  IconUserCircle,
  LinkButton,
  Popover,
  SearchButton,
  Select
} from '@swaggerhub-portal/component-library'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'
import { useHydrated } from 'remix-utils/use-hydrated'

import { defaultBackgroundColor } from './constants'
import { SearchModalDesktop } from '../SearchModal/desktop'
import { DarkModeToggleLoggedOut } from '~/components/DarkModeToggleLoggedOut'
import { DebugUI } from '~/components/DebugUI'
import { Profile } from '~/components/Profile'
import { usePageLayout } from '~/hooks/usePageLayout'
import { CONSUMER_PATHNAMES } from '~/routes/sp-portal/constants'
import { LandingProduct } from '~/types/product'
import { UserInfo } from '~/types/user'

export interface TopBarDesktopProps {
  accentColor?: string,
  logo?: string,
  name?: string,
  user?: UserInfo,
  baseUrl?: string,
  fontFamily?: string,
  currentProduct?: LandingProduct,
  products?: Array<LandingProduct>,
  accentDivider?: boolean,
  disabled?: boolean,
}

export const TopBarDesktop = ({
  accentColor,
  logo,
  name,
  user,
  baseUrl,
  fontFamily,
  currentProduct,
  products,
  accentDivider = false,
  disabled = false,
}: TopBarDesktopProps) => {

  const { t } = useTranslation()
  
  const location = useLocation()

  const ProfileComponent = useCallback(props => <Profile user={user || {}} isConsumerSide={true} {...props} />, [user])

  const navigate = useNavigate()

  const { consumerTopBar } = usePageLayout()

  const [searchModalOpen, setSearchModalOpen] = useState(false)

  const onChangeProduct = useCallback(
    (product: LandingProduct) => navigate(`/${product.slug}`),
    []
  )

  const isHydrated = useHydrated()

  const showDebugMenu = isHydrated ? localStorage.getItem('portal.showDebugMenu') === 'true' : false
  
  const isStatusPage = CONSUMER_PATHNAMES.includes(location.pathname)

  return (
    <div>
      <nav
        className={clsx(
          'w-full flex items-center justify-between h-[3.75rem] mx-auto bg-white dark:bg-neutral-98 z-50 relative',
          !isStatusPage && 'border-neutral-30 dark:border-neutral-80 border-b border-solid'
        )}
        aria-label={t('consumer.topBar.ariaLabel')}
        data-testid="topbar-desktop"
      >
        {disabled && <div data-testid="bg-stripes" className="absolute w-full h-full bg-stripes bg-neutral-05 dark:bg-neutral-95" />}
        <div className="flex flex-grow-0 flex-shrink-0">
          <Link
            to={baseUrl || '/'}
            className={clsx('px-4 py-1.5 flex items-center duration-200 h-[3.75rem] transition-color', disabled && 'pointer-events-none')}
            aria-label={t('consumer.topBar.landingPageLink.ariaLabel')}
          >
            {logo ? <img className={clsx('w-auto h-8 max-w-full z-[1]', disabled && 'opacity-80')} src={logo} alt="logo" /> : <div className="font-semibold text-body1 text-neutral-98 dark:text-neutral-40 z-[1] h-full line-clamp-2 max-w-[500px] break-all content-center">{name}</div>}
          </Link>
        </div>
        {
          products?.length && currentProduct?.slug ? (
            <div className="flex-grow-0 flex-shrink-0 pl-10">
              <Select
                name="product"
                accentColor={accentColor}
                className="!w-[13.5rem]"
                optionsClassName="!w-[13.5rem] !max-h-50 rounded border border-[#D8DDE4] dark:border-none shadow-[0_2px_4px_0_rgba(0,0,0,0.12)]"
                optionClassName="!whitespace-pre-wrap !break-all"
                theme="neutral"
                defaultValue={currentProduct}
                options={products}
                onChange={onChangeProduct}
                aria-label={t('consumer.topBar.productSelect.ariaLabel')}
                disabled={disabled}
                buttonClassName={clsx(disabled && '!bg-inherit')}
                size="s"
              />
            </div>
          ) : null
        }
        <div className="flex-1" />
        {!consumerTopBar?.search.disabled && (
          <div className="flex-grow-0 flex-shrink-0 w-[264px] mx-4">
            {isHydrated && <SearchButton onClick={() => setSearchModalOpen(true)} disabled={disabled} />}
          </div>
        )}
        <div className="gap-x-6 flex items-center justify-end flex-grow-0 flex-shrink-0 h-[3.75rem] z-[99]">
          {showDebugMenu && <DebugUI />}
          {
            user?.email ?
              <Popover
                data-testid="profile-button"
                overlayClassName="-left-[296px] mt-2"
                triggerClassName="outline-none"
                trigger={() => (
                  <IconSegment
                    label={t('consumer.topBar.profile.ariaLabel')}
                    icon={<IconUserCircle width={20} height={20} />}
                    clickable={!disabled}
                    className={clsx(
                      'h-[3.688rem] bg-white text-neutral-70 hover:bg-neutral-10',
                      'dark:bg-neutral-98 dark:text-neutral-40 dark:hover:bg-neutral-80',
                      disabled && 'bg-transparent hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent dark:hover:text-neutral-40 opacity-70',
                    )}
                  />
                )}
                overlay={ProfileComponent}
                disabled={disabled}
              />
              :
              <span className="flex gap-4">
                <DarkModeToggleLoggedOut accentColor={accentColor} />
                <LinkButton
                  to="/services/api/login"
                  type="secondary"
                  data-testid="login-button"
                  size="m"
                  className={clsx(
                    'mr-4 w-[104px] justify-center hover:bg-neutral-80 hover:text-neutral-00 dark:hover:bg-neutral-30 dark:hover:text-neutral-98 hover:!border-transparent'
                  )}
                  style={{ borderColor: accentColor, fontFamily }}
                >
                  {t('components.ui.buttons.logIn.label')}
                </LinkButton>
              </span>

          }
        </div>
      </nav>

      {accentDivider && <div
        data-testid="divider"
        className={clsx('h-1.5')}
        style={{ backgroundColor: accentColor || defaultBackgroundColor }}
      />}
      {searchModalOpen && <SearchModalDesktop
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)} 
        accentColor={accentColor}
        products={products}
        currentProduct={currentProduct}
      />}
    </div >
  )
}